/**
 * Daily Form Tab - Excel-like daily cash-up form
 */
import { useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertCircle, Printer, CheckCircle, Save, Loader2 } from "lucide-react";
import { formatCurrency } from "./constants";

export const DailyFormTab = ({
  selectedDate,
  existingCashupForDate,
  dailyForm,
  setDailyForm,
  summaryData,
  openingFloat = 0,
  onPrintDaily,
  onFinishClick,
  onSaveDraft,
  savingDraft,
}) => {
  const isCompleted = !!existingCashupForDate;
  // Calculate totals
  const notesTotal = 
    (dailyForm.note200 * 200) + 
    (dailyForm.note100 * 100) + 
    (dailyForm.note50 * 50) + 
    (dailyForm.note20 * 20) + 
    (dailyForm.note10 * 10);
  
  const totalCash = notesTotal + dailyForm.change;
  
  // Expected Cash (must match backend cashup.py calculate_cashup_summary):
  //   cash_sales + account_receipts_cash − petty_cash − cash_refunds
  //   − loan_cash_outs − withdrawal_cash_outs
  // Opening float is NOT added — the user doesn't count it with the day's takings.
  const expectedCash =
    (summaryData?.cash_sales || 0)
    + (summaryData?.account_receipts_cash || 0)
    - (dailyForm.pettyCash || 0)
    - (dailyForm.cashRefunds || 0)
    - (summaryData?.loan_cash_outs || 0)
    - (summaryData?.withdrawal_cash_outs || 0);
  const cashBalance = totalCash - expectedCash;
  
  // Card balance: What we got on speedpoint vs total expected card (card_sales + account receipts card portion)
  const expectedCard = (summaryData?.card_sales || 0) + (summaryData?.account_receipts_card || 0);
  const cardBalance = dailyForm.speedpointAmount - expectedCard;
  
  // Final balance combines both
  const finalBalance = cashBalance + cardBalance;

  return (
    <>
      {/* Show warning if cashup already exists for this date */}
      {existingCashupForDate && (
        <div className="mb-4 p-4 bg-amber-100 border border-amber-400 rounded-lg">
          <div className="flex items-center gap-2 text-amber-800">
            <AlertCircle className="h-5 w-5" />
            <span className="font-semibold">Cash-up already completed for {selectedDate}</span>
          </div>
          <p className="text-sm text-amber-700 mt-1">
            Cash-Up No: {existingCashupForDate.cashup_no} - Only ONE cash-up per day is allowed.
          </p>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Column 1: Sales Summary */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-100 border-b py-2">
            <CardTitle className="text-base font-bold text-blue-900">DAILY CASH-UP</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <tbody>
                {/* Date */}
                <tr className="border-b">
                  <td className="py-2 px-3 bg-gray-50 font-medium w-1/3">DATE</td>
                  <td className="py-2 px-3 font-semibold" colSpan={2}>
                    {new Date(selectedDate).toLocaleDateString('en-ZA')}
                  </td>
                </tr>
                {/* Cash-Up No - Editable */}
                <tr className="border-b">
                  <td className="py-2 px-3 bg-gray-50 font-medium">CASH UP NO</td>
                  <td className="py-1 px-1" colSpan={2}>
                    <Input
                      type="text"
                      value={dailyForm.cashUpNo}
                      onChange={(e) => setDailyForm({...dailyForm, cashUpNo: e.target.value})}
                      className="h-8 bg-green-50 border-green-300 focus:border-green-500"
                      placeholder="e.g., 320"
                      data-testid="daily-cashup-no"
                      readOnly={isCompleted}
                    />
                  </td>
                </tr>
                {/* Petty Cash - Editable */}
                <tr className="border-b">
                  <td className="py-2 px-3 bg-gray-50 font-medium">PETTY CASH</td>
                  <td className="py-1 px-1">
                    <Input
                      type="number"
                      step="0.01"
                      value={dailyForm.pettyCash || ''}
                      onChange={(e) => setDailyForm({...dailyForm, pettyCash: parseFloat(e.target.value) || 0})}
                      className="h-8 bg-green-50 border-green-300 focus:border-green-500 text-right"
                      data-testid="daily-petty-cash"
                      readOnly={isCompleted}
                    />
                  </td>
                  <td className="py-2 px-1 text-right text-xs text-gray-500 w-24">Exp: {formatCurrency(summaryData?.petty_cash || 0)}</td>
                </tr>
                {/* Loan Cash-Outs - System calculated */}
                {(summaryData?.loan_cash_outs > 0) && (
                  <tr className="border-b bg-amber-50">
                    <td className="py-2 px-3 font-medium text-amber-700">LOAN CASH-OUTS</td>
                    <td className="py-2 px-3 text-right font-bold text-amber-700" colSpan={2}>
                      -{formatCurrency(summaryData?.loan_cash_outs || 0)}
                    </td>
                  </tr>
                )}
                {/* Cash Sales - Direct from system */}
                <tr className="border-b bg-blue-50">
                  <td className="py-2 px-3 font-medium">CASH SALES</td>
                  <td className="py-2 px-3 text-right font-bold text-blue-700" colSpan={2}>
                    {formatCurrency(summaryData?.cash_sales || 0)}
                  </td>
                </tr>
                {/* Cash Refunds - Editable (binds to cashRefunds so the variance formula matches) */}
                <tr className="border-b">
                  <td className="py-2 px-3 bg-gray-50 font-medium">CASH REFUNDS</td>
                  <td className="py-1 px-1">
                    <Input
                      type="number"
                      step="0.01"
                      value={dailyForm.cashRefunds || ''}
                      onChange={(e) => {
                        const total = parseFloat(e.target.value) || 0;
                        // Mirror to `refunds` for backward-compat with old print/email templates that still read it.
                        setDailyForm({...dailyForm, cashRefunds: total, refunds: total});
                      }}
                      className="h-8 bg-green-50 border-green-300 focus:border-green-500 text-right"
                      data-testid="daily-refunds"
                      readOnly={isCompleted}
                    />
                  </td>
                  <td className="py-2 px-1 text-right text-xs text-gray-500 w-24">
                    Exp: {formatCurrency(summaryData?.cash_refunds || 0)}
                    {(summaryData?.card_refunds > 0) && (
                      <div className="text-blue-600">Card: {formatCurrency(summaryData?.card_refunds)}</div>
                    )}
                    {(summaryData?.account_refunds > 0) && (
                      <div className="text-amber-600">Acc: {formatCurrency(summaryData?.account_refunds)}</div>
                    )}
                  </td>
                </tr>
                {/* Acc Sales - System */}
                <tr className="border-b">
                  <td className="py-2 px-3 bg-gray-50 font-medium">ACC SALES</td>
                  <td className="py-2 px-3 text-right font-semibold">{formatCurrency(summaryData?.account_sales)}</td>
                  <td className="py-2 px-1 text-right text-xs text-gray-500 w-24">Exp: {formatCurrency(summaryData?.account_sales)}</td>
                </tr>
                {/* Acc Payment - Editable */}
                <tr className="border-b">
                  <td className="py-2 px-3 bg-gray-50 font-medium">ACC PAYMENT</td>
                  <td className="py-1 px-1">
                    <Input
                      type="number"
                      step="0.01"
                      value={dailyForm.accPayment || ''}
                      onChange={(e) => setDailyForm({...dailyForm, accPayment: parseFloat(e.target.value) || 0})}
                      className="h-8 bg-green-50 border-green-300 focus:border-green-500 text-right"
                      data-testid="daily-acc-payment"
                      readOnly={isCompleted}
                    />
                  </td>
                  <td className="py-2 px-1 text-right text-xs text-gray-500 w-24">Exp: {formatCurrency(summaryData?.account_receipts || 0)}</td>
                </tr>
                {/* Card Sales - System */}
                <tr className="border-b bg-blue-50">
                  <td className="py-2 px-3 font-medium">CARD SALES</td>
                  <td className="py-2 px-3 text-right font-bold text-blue-700" colSpan={2}>
                    {formatCurrency(dailyForm.speedpointAmount)}
                  </td>
                </tr>
                {/* Total Sales (Net Revenue) — refunds and petty cash are
                    already excluded server-side in cashup.py (build_sales_date_query
                    with exclude_refunds=True, exclude_petty_cash=True, exclude_voided=True),
                    so we just display total_sales straight. Subtracting them again here
                    was double-counting and incorrectly treating petty cash as a revenue
                    reversal. */}
                <tr className="bg-blue-200">
                  <td className="py-3 px-3 font-bold text-blue-900">SALES</td>
                  <td className="py-3 px-3 text-right font-bold text-xl text-blue-900" colSpan={2}>
                    {formatCurrency(summaryData?.total_sales || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Column 2: Cash Count */}
        <Card className="border-2 border-green-200">
          <CardHeader className="bg-green-100 border-b py-2">
            <CardTitle className="text-base font-bold text-green-900">CASH COUNT</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-3 text-left font-medium">CASH</th>
                  <th className="py-2 px-2 text-center font-medium w-16">QTY</th>
                  <th className="py-2 px-3 text-right font-medium">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {/* R 200 */}
                <tr className="border-b">
                  <td className="py-1 px-3">R 200.00</td>
                  <td className="py-1 px-1">
                    <Input
                      type="number"
                      min="0"
                      value={dailyForm.note200 || ''}
                      onChange={(e) => setDailyForm({...dailyForm, note200: parseInt(e.target.value) || 0})}
                      className="h-7 w-14 text-center bg-green-50 border-green-300"
                      data-testid="daily-note-200"
                      readOnly={isCompleted}
                    />
                  </td>
                  <td className="py-1 px-3 text-right font-semibold">{formatCurrency(dailyForm.note200 * 200)}</td>
                </tr>
                {/* R 100 */}
                <tr className="border-b">
                  <td className="py-1 px-3">R 100.00</td>
                  <td className="py-1 px-1">
                    <Input
                      type="number"
                      min="0"
                      value={dailyForm.note100 || ''}
                      onChange={(e) => setDailyForm({...dailyForm, note100: parseInt(e.target.value) || 0})}
                      className="h-7 w-14 text-center bg-green-50 border-green-300"
                      data-testid="daily-note-100"
                      readOnly={isCompleted}
                    />
                  </td>
                  <td className="py-1 px-3 text-right font-semibold">{formatCurrency(dailyForm.note100 * 100)}</td>
                </tr>
                {/* R 50 */}
                <tr className="border-b">
                  <td className="py-1 px-3">R 50.00</td>
                  <td className="py-1 px-1">
                    <Input
                      type="number"
                      min="0"
                      value={dailyForm.note50 || ''}
                      onChange={(e) => setDailyForm({...dailyForm, note50: parseInt(e.target.value) || 0})}
                      className="h-7 w-14 text-center bg-green-50 border-green-300"
                      data-testid="daily-note-50"
                      readOnly={isCompleted}
                    />
                  </td>
                  <td className="py-1 px-3 text-right font-semibold">{formatCurrency(dailyForm.note50 * 50)}</td>
                </tr>
                {/* R 20 */}
                <tr className="border-b">
                  <td className="py-1 px-3">R 20.00</td>
                  <td className="py-1 px-1">
                    <Input
                      type="number"
                      min="0"
                      value={dailyForm.note20 || ''}
                      onChange={(e) => setDailyForm({...dailyForm, note20: parseInt(e.target.value) || 0})}
                      className="h-7 w-14 text-center bg-green-50 border-green-300"
                      data-testid="daily-note-20"
                      readOnly={isCompleted}
                    />
                  </td>
                  <td className="py-1 px-3 text-right font-semibold">{formatCurrency(dailyForm.note20 * 20)}</td>
                </tr>
                {/* R 10 */}
                <tr className="border-b">
                  <td className="py-1 px-3">R 10.00</td>
                  <td className="py-1 px-1">
                    <Input
                      type="number"
                      min="0"
                      value={dailyForm.note10 || ''}
                      onChange={(e) => setDailyForm({...dailyForm, note10: parseInt(e.target.value) || 0})}
                      className="h-7 w-14 text-center bg-green-50 border-green-300"
                      data-testid="daily-note-10"
                      readOnly={isCompleted}
                    />
                  </td>
                  <td className="py-1 px-3 text-right font-semibold">{formatCurrency(dailyForm.note10 * 10)}</td>
                </tr>
                {/* Change - Editable */}
                <tr className="border-b bg-amber-50">
                  <td className="py-1 px-3 font-medium">CHANGE</td>
                  <td className="py-1 px-1" colSpan={2}>
                    <Input
                      type="number"
                      step="0.01"
                      value={dailyForm.change || ''}
                      onChange={(e) => setDailyForm({...dailyForm, change: parseFloat(e.target.value) || 0})}
                      className="h-7 bg-green-50 border-green-300 text-right"
                      data-testid="daily-change"
                      readOnly={isCompleted}
                    />
                  </td>
                </tr>
                {/* Total Cash - Calculated */}
                <tr className="bg-green-200">
                  <td className="py-2 px-3 font-bold text-green-900">TOTAL CASH</td>
                  <td className="py-2 px-3 text-right font-bold text-lg text-green-900" colSpan={2}>
                    {formatCurrency(totalCash)}
                  </td>
                </tr>
                {/* Expected vs Counted */}
                <tr className="border-b bg-gray-50">
                  <td className="py-2 px-3 text-xs text-gray-500" colSpan={2}>Expected:</td>
                  <td className="py-2 px-3 text-right text-sm font-medium">
                    {formatCurrency(expectedCash)}
                  </td>
                </tr>
                {/* Cash Balance */}
                <tr className={`${cashBalance < 0 ? 'bg-red-100' : cashBalance > 0 ? 'bg-blue-100' : 'bg-green-100'}`}>
                  <td className="py-2 px-3 font-bold" colSpan={2}>BALANCE</td>
                  <td className={`py-2 px-3 text-right font-bold text-lg ${cashBalance < 0 ? 'text-red-700' : cashBalance > 0 ? 'text-blue-700' : 'text-green-700'}`}>
                    {formatCurrency(cashBalance)}
                  </td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Column 3: Speedpoint & Final Balance */}
        <Card className="border-2 border-purple-200">
          <CardHeader className="bg-purple-100 border-b py-2">
            <CardTitle className="text-base font-bold text-purple-900">SPEEDPOINT (CARD)</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-3 text-left font-medium">SPEEDPOINT</th>
                  <th className="py-2 px-2 text-center font-medium w-20">BATCH NO</th>
                  <th className="py-2 px-3 text-right font-medium">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {/* Speedpoint Entry */}
                <tr className="border-b">
                  <td className="py-2 px-3">Card Terminal</td>
                  <td className="py-1 px-1">
                    <Input
                      type="text"
                      value={dailyForm.speedpointBatchNo}
                      onChange={(e) => setDailyForm({...dailyForm, speedpointBatchNo: e.target.value})}
                      className="h-7 w-16 text-center bg-green-50 border-green-300"
                      placeholder="Batch"
                      data-testid="daily-speedpoint-batch"
                      readOnly={isCompleted}
                    />
                  </td>
                  <td className="py-1 px-1">
                    <Input
                      type="number"
                      step="0.01"
                      value={dailyForm.speedpointAmount || ''}
                      onChange={(e) => setDailyForm({...dailyForm, speedpointAmount: parseFloat(e.target.value) || 0})}
                      className="h-7 bg-green-50 border-green-300 text-right"
                      data-testid="daily-speedpoint-amount"
                      readOnly={isCompleted}
                    />
                  </td>
                </tr>
                {/* Expected Card */}
                <tr className="border-b bg-gray-50">
                  <td className="py-2 px-3 text-xs text-gray-500" colSpan={2}>Expected:</td>
                  <td className="py-2 px-3 text-right text-sm font-medium">
                    {formatCurrency((summaryData?.card_sales || 0) + (summaryData?.account_receipts_card || 0))}
                  </td>
                </tr>
                {/* Card Balance */}
                <tr className={`${cardBalance < 0 ? 'bg-red-100' : cardBalance > 0 ? 'bg-blue-100' : 'bg-green-100'}`}>
                  <td className="py-2 px-3 font-bold" colSpan={2}>BALANCE</td>
                  <td className={`py-2 px-3 text-right font-bold text-lg ${cardBalance < 0 ? 'text-red-700' : cardBalance > 0 ? 'text-blue-700' : 'text-green-700'}`}>
                    {formatCurrency(cardBalance)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Final Balance Section */}
            <div className="mt-4 border-t-2 border-purple-300">
              <div className={`p-4 ${finalBalance < 0 ? 'bg-red-200' : finalBalance > 0 ? 'bg-blue-200' : 'bg-green-200'}`}>
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">FINAL BALANCE</span>
                  <span className={`font-bold text-2xl ${finalBalance < 0 ? 'text-red-700' : finalBalance > 0 ? 'text-blue-700' : 'text-green-700'}`} data-testid="daily-final-balance">
                    {formatCurrency(finalBalance)}
                  </span>
                </div>
                <p className={`text-sm mt-1 ${finalBalance < 0 ? 'text-red-600' : finalBalance > 0 ? 'text-blue-600' : 'text-green-600'}`}>
                  {finalBalance < 0 ? 'SHORT' : finalBalance > 0 ? 'OVER' : 'BALANCED'}
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="p-4 border-t space-y-2">
              <Button onClick={onSaveDraft} className="w-full" variant="outline" disabled={savingDraft || !!existingCashupForDate} data-testid="save-draft-btn">
                {savingDraft ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
              <Button onClick={onPrintDaily} className="w-full" variant="outline" data-testid="print-daily-btn">
                <Printer className="h-4 w-4 mr-2" />
                Print Daily Cash-Up
              </Button>
              <Button 
                onClick={onFinishClick} 
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="finish-save-btn"
                disabled={!!existingCashupForDate}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Finish & Save (Excel + Email)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default DailyFormTab;
